import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Save, Calculator, Plus as PlusIcon } from 'lucide-react';
import { useKurs } from '@/hooks/useKurs';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

const PARFAROME_GRADE = ['Deluxe', 'Premium', 'Exclusive'];

export default function DataBibitSampleForm({ item, onClose, onSaved, hidePrices = false, defaultVendor = '' }) {
  const qc = useQueryClient();
  const isEdit = !!item?.id;
  const { kurs, kursEuro } = useKurs();

  const [form, setForm] = useState({
    vendor: item?.vendor || defaultVendor || '',
    grade: item?.grade || '', // Khusus Parfarome
    nama_bibit: item?.nama_bibit || '',
    mata_uang: item?.mata_uang || 'IDR',
    harga_valas: item?.mata_uang !== 'IDR' && item?.harga ? item.harga.toString() : '',
    harga_rupiah: item?.mata_uang === 'IDR' && item?.harga ? item.harga.toString() : '',
    catatan: item?.catatan || '',
    status_ketersediaan: item?.status_ketersediaan || 'Tersedia',
    _lastEdited: item?.mata_uang && item.mata_uang !== 'IDR' ? 'valas' : 'rupiah',
    ppn_type: item?.ppn_type || 'include',
    ppn_percentage: item?.ppn_percentage ?? 11,
    harga_sebelum_ppn: item?.harga_sebelum_ppn ?? (item?.mata_uang === 'IDR' && item?.harga ? item.harga.toString() : ''),
  });

  const { data: bibitList = [] } = useQuery({
    queryKey: ['bibit-sample'],
    queryFn: () => base44.entities.BibitSample.list()
  });

  const vendorOptions = [...new Set(bibitList.map(b => b.vendor).filter(Boolean))].sort();

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // USD → Rupiah otomatis (base price only)
  useEffect(() => {
    if (form.mata_uang === 'USD' && form._lastEdited === 'valas' && form.harga_valas && kurs) {
      setForm(p => ({ ...p, harga_sebelum_ppn: (parseFloat(p.harga_valas) * kurs).toFixed(0) }));
    }
  }, [form.harga_valas, form.mata_uang, kurs, form._lastEdited]);

  // EUR → Rupiah otomatis (base price only)
  useEffect(() => {
    if (form.mata_uang === 'EUR' && form._lastEdited === 'valas' && form.harga_valas && kursEuro) {
      setForm(p => ({ ...p, harga_sebelum_ppn: (parseFloat(p.harga_valas) * kursEuro).toFixed(0) }));
    }
  }, [form.harga_valas, form.mata_uang, kursEuro, form._lastEdited]);

  // Rupiah → USD/EUR otomatis
  useEffect(() => {
    if (form._lastEdited === 'rupiah' && form.harga_sebelum_ppn) {
      if (form.mata_uang === 'USD' && kurs) {
        setForm(p => ({ ...p, harga_valas: (parseFloat(p.harga_sebelum_ppn) / kurs).toFixed(4) }));
      } else if (form.mata_uang === 'EUR' && kursEuro) {
        setForm(p => ({ ...p, harga_valas: (parseFloat(p.harga_sebelum_ppn) / kursEuro).toFixed(4) }));
      }
    }
  }, [form.harga_sebelum_ppn, form.mata_uang, kurs, kursEuro, form._lastEdited]);

  // Hitung Harga Sesudah PPN otomatis
  useEffect(() => {
    if (form.harga_sebelum_ppn !== '') {
      let awal = parseFloat(form.harga_sebelum_ppn) || 0;
      if (form.ppn_type === 'exclude') {
        let pct = parseFloat(form.ppn_percentage) || 0;
        let akhir = awal * (1 + pct / 100);
        setForm(p => ({ ...p, harga_rupiah: akhir.toFixed(0) }));
      } else {
        setForm(p => ({ ...p, harga_rupiah: awal.toFixed(0) }));
      }
    }
  }, [form.harga_sebelum_ppn, form.ppn_type, form.ppn_percentage]);

  const handlePpnTypeChange = (newType) => {
    if (newType === form.ppn_type) return;
    setForm(p => ({ ...p, ppn_type: newType }));
  };

  const handlePpnPctChange = (newPct) => {
    setForm(p => ({ ...p, ppn_percentage: newPct }));
  };

  const saveMut = useMutation({
    mutationFn: (data) => 
      isEdit 
        ? base44.entities.BibitSample.update(item.id, data)
        : base44.entities.BibitSample.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bibit-sample'] });
      onSaved?.();
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalHarga = hidePrices ? 0 : (form.mata_uang === 'IDR' ? parseFloat(form.harga_rupiah) : parseFloat(form.harga_valas));
    if (!form.vendor.trim() || !form.nama_bibit.trim() || (!hidePrices && (!finalHarga || finalHarga <= 0))) return;
    
    saveMut.mutate({
      vendor: form.vendor,
      grade: form.grade,
      nama_bibit: form.nama_bibit,
      mata_uang: form.mata_uang,
      harga: finalHarga,
      catatan: form.catatan,
      status_ketersediaan: form.status_ketersediaan,
      ppn_type: form.ppn_type,
      ppn_percentage: form.ppn_percentage ? parseFloat(form.ppn_percentage) : 11,
      harga_sebelum_ppn: form.harga_sebelum_ppn ? parseFloat(form.harga_sebelum_ppn) : finalHarga
    });
  };

  const isParfarome = form.vendor.toLowerCase().includes('parfarome');

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold">{isEdit ? 'Edit' : 'Tambah'} Data Bibit</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Vendor *</label>
              <SearchableCombobox
                value={form.vendor}
                onValueChange={v => {
                  set('vendor', v);
                  if (!v.toLowerCase().includes('parfarome')) set('grade', '');
                }}
                options={vendorOptions}
                placeholder="Pilih vendor *"
                searchPlaceholder="Ketik untuk cari..."
              />
            </div>

            {isParfarome && (
              <div>
                <label className="text-xs font-medium text-amber-600 block mb-1.5">Grade Parfarome *</label>
                <div className="flex gap-2">
                  {PARFAROME_GRADE.map(g => (
                    <button key={g} type="button" onClick={() => set('grade', g)}
                      className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${form.grade === g ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-300 text-amber-700 hover:border-amber-500'}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Nama Bibit *</label>
              <Input 
                value={form.nama_bibit} 
                onChange={e => set('nama_bibit', e.target.value)} 
                placeholder="Contoh: Baccarat Rouge 540" 
                required 
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Ketersediaan Stock *</label>
              <div className="flex gap-2">
                {['Tersedia', 'Tidak Tersedia'].map(status => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => set('status_ketersediaan', status)}
                    className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                      form.status_ketersediaan === status
                        ? (status === 'Tersedia' ? 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm' : 'bg-rose-50 text-rose-700 border-rose-300 shadow-sm')
                        : 'border-border text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {!hidePrices && (
              <>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Mata Uang *</label>
                  <div className="flex gap-2">
                    {['IDR', 'USD', 'EUR'].map(curr => (
                      <button
                        key={curr}
                        type="button"
                        onClick={() => {
                          setForm(p => ({ ...p, mata_uang: curr, _lastEdited: curr === 'IDR' ? 'rupiah' : 'valas' }));
                        }}
                        className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                          form.mata_uang === curr
                            ? (curr === 'USD' ? 'bg-amber-50 text-amber-700 border-amber-300 shadow-sm' : curr === 'EUR' ? 'bg-blue-50 text-blue-700 border-blue-300 shadow-sm' : 'bg-green-50 text-green-700 border-green-300 shadow-sm')
                            : 'border-border text-muted-foreground hover:border-accent/50'
                        }`}
                      >
                        {curr === 'IDR' ? 'Rupiah (Rp)' : curr === 'USD' ? 'USD ($)' : 'EUR (€)'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Status PPN Harga Beli</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handlePpnTypeChange('include')}
                      className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                        form.ppn_type === 'include'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm'
                          : 'border-border text-muted-foreground hover:border-accent/50'
                      }`}
                    >
                      Include PPN
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePpnTypeChange('exclude')}
                      className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                        form.ppn_type === 'exclude'
                          ? 'bg-amber-50 text-amber-700 border-amber-300 shadow-sm'
                          : 'border-border text-muted-foreground hover:border-accent/50'
                      }`}
                    >
                      Exclude PPN
                    </button>
                  </div>
                </div>

                {form.ppn_type === 'exclude' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Persentase PPN (%)</label>
                    <Input 
                      type="number" step="0.1" min="0"
                      value={form.ppn_percentage} 
                      onChange={e => handlePpnPctChange(Number(e.target.value))} 
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {form.mata_uang !== 'IDR' && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">
                        Harga {form.mata_uang === 'USD' ? 'USD ($)' : 'EUR (€)'}
                      </label>
                      <Input 
                        type="number" step="0.01" min="0"
                        value={form.harga_valas} 
                        onChange={e => setForm(p => ({ ...p, harga_valas: e.target.value, _lastEdited: 'valas' }))} 
                        placeholder="0.00" 
                        required={form.mata_uang !== 'IDR'}
                      />
                    </div>
                  )}
                  <div className={form.mata_uang === 'IDR' ? 'col-span-2' : ''}>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {form.ppn_type === 'include' ? 'Harga Beli (Rp) *' : 'Harga Awal Sebelum PPN (Rp) *'}
                    </label>
                    <Input 
                      type="number" min="0" step="1"
                      value={form.harga_sebelum_ppn} 
                      onChange={e => setForm(p => ({ ...p, harga_sebelum_ppn: e.target.value, _lastEdited: 'rupiah' }))} 
                      placeholder="0" 
                      required 
                    />
                  </div>
                </div>

                {form.ppn_type === 'exclude' && form.harga_sebelum_ppn && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="text-amber-800">Harga Sebelum PPN:</span>
                      <span className="font-semibold text-amber-900">Rp {parseFloat(form.harga_sebelum_ppn).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs mb-2">
                      <span className="text-amber-800">PPN ({form.ppn_percentage}%):</span>
                      <span className="font-semibold text-amber-900">+ Rp {Math.round(parseFloat(form.harga_sebelum_ppn) * (parseFloat(form.ppn_percentage) || 0) / 100).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm border-t border-amber-200/50 pt-2 mt-2">
                      <span className="font-bold text-amber-900">Harga Sesudah PPN:</span>
                      <span className="font-black text-amber-700">Rp {parseFloat(form.harga_rupiah || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}
                {form.ppn_type === 'include' && form.harga_sebelum_ppn && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-emerald-800">Harga (Sudah Termasuk PPN):</span>
                      <span className="font-black text-emerald-700">Rp {parseFloat(form.harga_rupiah || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}

                {form.mata_uang === 'USD' && form.harga_valas && form.harga_sebelum_ppn && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5 mt-2">
                    <Calculator className="w-3.5 h-3.5" /> ${parseFloat(form.harga_valas).toFixed(2)} × Rp {kurs.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = <strong>Rp {(parseFloat(form.harga_valas) * kurs).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </p>
                )}
                {form.mata_uang === 'EUR' && form.harga_valas && form.harga_sebelum_ppn && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-1.5 mt-2">
                    <Calculator className="w-3.5 h-3.5" /> €{parseFloat(form.harga_valas).toFixed(2)} × Rp {kursEuro.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = <strong>Rp {(parseFloat(form.harga_valas) * kursEuro).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </p>
                )}
              </>
            )}

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Catatan (Opsional)</label>
              <Input 
                value={form.catatan} 
                onChange={e => set('catatan', e.target.value)} 
                placeholder="Catatan tambahan" 
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border mt-6">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button 
              type="submit" 
              disabled={saveMut.isPending || !form.vendor.trim() || !form.nama_bibit.trim() || (!hidePrices && !form.harga_rupiah)} 
              className="flex-1 gap-2"
            >
              <Save className="w-4 h-4" />
              {saveMut.isPending ? 'Menyimpan...' : 'Simpan Data'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
